import React from 'react';
import { Box, Button, Fade, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

// Verdes de la etiqueta de capa (#16a34a → #4ade80) sobre el cristal oscuro de cabecera y barra inferior (#161616)
const GREEN = 'rgba(34, 197, 94, 1)';
const GREEN_LIGHT = '#4ade80';

const spin = keyframes`to { transform: rotate(360deg); }`;

// El aviso de "tarda más" entra a los 4 s con una animación CSS con retardo, sin temporizadores. Mientras espera
// (fill-mode both) se queda en altura 0, padding incluido, así la tarjeta no reserva el hueco
const reveal = keyframes`
  from { max-height: 0; padding-top: 0; opacity: 0; }
  to { max-height: 5em; padding-top: 8px; opacity: 1; }
`;

const glass = {
  bgcolor: 'rgba(22, 22, 22, 0.9)',
  backdropFilter: 'blur(16px) saturate(140%)',
  WebkitBackdropFilter: 'blur(16px) saturate(140%)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 18px 48px -12px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.2)',
  color: '#fff',
  fontFamily: '"Open Sans", sans-serif',
};

// Barrido de radar en vez de un spinner genérico: anillo, línea de barrido con estela y punto central
const Radar = ({ size }) => (
  <Box
    aria-hidden
    sx={{
      position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: '50%', overflow: 'hidden',
      border: '1px solid rgba(74, 222, 128, 0.45)',
      background: `radial-gradient(circle closest-side, transparent 46%, rgba(74, 222, 128, 0.22) 47% 51%, transparent 52%), rgba(34, 197, 94, 0.08)`,
      '&::before': {
        content: '""', position: 'absolute', inset: 0,
        background: `conic-gradient(rgba(34, 197, 94, 0) 0deg 230deg, rgba(74, 222, 128, 0.55) 345deg, ${GREEN_LIGHT} 360deg)`,
        animation: `${spin} 1.8s linear infinite`,
      },
      '&::after': {
        content: '""', position: 'absolute', top: '50%', left: '50%', width: 4, height: 4, margin: '-2px',
        borderRadius: '50%', bgcolor: GREEN_LIGHT, boxShadow: `0 0 6px ${GREEN}`,
      },
      '@media (prefers-reduced-motion: reduce)': { '&::before': { animation: 'none' } },
    }}
  />
);

const TimeChip = ({ children, sx }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0, px: 0.75, py: 0.25, borderRadius: '6px', bgcolor: 'rgba(34, 197, 94, 0.14)', color: GREEN_LIGHT,
      fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.4, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Aviso de carga encima de un mapa. No bloquea el mapa (pointer-events: none) y solo aparece si la carga pasa de
 * `delay` ms (400 por defecto: lo que sale de Redis no llega a mostrarlo). `time` = 'YYYY-MM-DD HH:mm:ss' (UTC).
 * - compact = false: tarjeta centrada sobre un velo, cuando el mapa todavía no tiene nada de lo pedido
 * - compact = true: píldora abajo, cuando ya se ve algo (la media hora del slider, la hora anterior) y falta el resto
 * - layer: lo que se está cargando; la píldora lo dice ("Loading Satellite Imagery") en vez del título
 * - dark = true: velo oscuro para mapas de fondo negro (Contrail); por defecto el velo claro de Kairos
 * - title: texto principal; por defecto 'Loading historical data'
 */
const MapLoadingOverlay = ({ open, compact, layer, time = '', dark = false, title = 'Loading historical data', delay = 400 }) => {
  const hour = `${time.slice(11, 16)} UTC`;
  return (
    <Fade in={open} style={{ transitionDelay: open ? `${delay}ms` : '0ms' }} unmountOnExit>
      <Box
        role="status"
        sx={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', p: 3,
          display: 'flex', justifyContent: 'center', alignItems: compact ? 'flex-end' : 'center',
          // Velo más denso detrás de la tarjeta: el mapa se aparta sin desaparecer
          background: compact ? 'none' : (dark
            ? 'radial-gradient(ellipse at center, rgba(10, 10, 10, 0.6), rgba(10, 10, 10, 0.25) 75%)'
            : 'radial-gradient(ellipse at center, rgba(250, 250, 248, 0.72), rgba(250, 250, 248, 0.3) 75%)'),
          backdropFilter: compact ? 'none' : 'blur(2px)',
        }}
      >
        {compact ? (
          <Box sx={{ ...glass, borderRadius: 99, pl: 0.75, pr: 0.875, py: 0.625, display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
            <Radar size={18} />
            <Typography noWrap sx={{ fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 600, minWidth: 0 }}>
              {layer ? `Loading ${layer}` : title}
            </Typography>
            <TimeChip sx={{ borderRadius: 99 }}>{hour}</TimeChip>
          </Box>
        ) : (
          <Box sx={{ ...glass, borderRadius: '18px', p: 2.25, width: 'min(400px, 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Radar size={40} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                  {title}
                </Typography>
                {/* Varias capas a la vez ("Contrails, Contrails RF, and Satellite Imagery") pasan a otra línea en vez de cortarse */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'inherit', fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.68)', minWidth: 0 }}>
                    {layer}
                  </Typography>
                  <TimeChip>{`${time.slice(0, 10)} ${hour}`}</TimeChip>
                </Box>
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: 'inherit', fontSize: '0.75rem', lineHeight: 1.45, color: 'rgba(255, 255, 255, 0.6)',
                textWrap: 'pretty', overflow: 'hidden', pl: '54px', animation: `${reveal} 300ms ease 4s both`, // 54 = radar + hueco
              }}
            >
              Data from the archive can take a few seconds.
            </Typography>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

/**
 * Aviso cuando la capa no se puede pintar. Antes los dos casos dejaban el mapa en blanco sin explicación:
 * - kind = 'empty': no hay datos (pmtiles 404, WFS sin features) → `empty` dice qué falta
 * - kind = 'error': falló la carga (red, backend, GeoServer) → Retry la repite
 * Sin onRetry no hay botón (p.ej. aviso de datos antiguos). `bottom` sube el aviso si algo tapa el pie del mapa
 */
export const MapNotice = ({ kind, empty, onRetry, bottom = 24 }) => kind ? (
  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom, zIndex: 1, px: 3, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
    <Box role="status" sx={{ ...glass, borderRadius: 99, pl: 1.5, pr: onRetry ? 0.5 : 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', pointerEvents: 'auto' }}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: kind === 'error' ? '#f87171' : 'rgba(255, 255, 255, 0.45)' }} />
      <Typography noWrap sx={{ fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 600, minWidth: 0 }}>
        {kind === 'error' ? 'Could not load the layer' : empty}
      </Typography>
      {onRetry && (
        <Button size="small" onClick={onRetry} sx={{ color: GREEN_LIGHT, minWidth: 0, px: 1.25, borderRadius: 99, textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}>
          Retry
        </Button>
      )}
    </Box>
  </Box>
) : null;

export default MapLoadingOverlay;
